import { useEffect, useState } from 'react';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: any;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: {
    bg_color?: string;
    text_color?: string;
    button_color?: string;
    button_text_color?: string;
    [key: string]: any;
  };
  isExpanded: boolean;
  viewportHeight: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  onEvent: (eventType: string, eventHandler: (...args: any[]) => void) => void;
  offEvent: (eventType: string, eventHandler: (...args: any[]) => void) => void;
}

export function useTelegramWebApp() {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Only load Telegram WebApp script if we're actually in Telegram
    // Check for Telegram WebApp by looking for initData in URL or Telegram object
    if (typeof window === 'undefined') return;

    // Check if already loaded
    if ((window as any).Telegram?.WebApp) {
      const tg = (window as any).Telegram.WebApp;
      // Only use it if we have initData (actually in Telegram)
      if (tg.initData || window.location.search.includes('tgWebAppStartParam') || window.location.search.includes('tgWebAppVersion')) {
        tg.ready();
        setWebApp(tg);
        setIsReady(true);
      }
      return;
    }

    // Check if we're likely in Telegram before loading script
    const isLikelyTelegram = 
      window.location.search.includes('tgWebAppStartParam') ||
      window.location.search.includes('tgWebAppVersion') ||
      window.location.hash.includes('tgWebAppStartParam') ||
      window.location.hash.includes('tgWebAppVersion') ||
      document.referrer.includes('telegram.org') ||
      document.referrer.includes('t.me');

    // Load script only if we're likely in Telegram
    if (isLikelyTelegram) {
      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-web-app.js';
      script.async = true;
      script.onload = () => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
          tg.ready();
          setWebApp(tg);
          setIsReady(true);
        }
      };
      document.head.appendChild(script);
    }
  }, []);

  return { webApp, isReady };
}

